// /app/components/ErrorBoundary.jsx
"use client";

import React, { Component } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";

// Higher-Order Component to inject theme as a prop
const withThemeProps = (WrappedComponent) => {
  return function ThemeComponent(props) {
    const theme = useTheme();
    return <WrappedComponent {...props} theme={theme} />;
  };
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    const { hasError, error } = this.state;
    const { theme } = this.props;

    if (hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: theme.palette.background.default
          }}>
          <Container>
            <Typography
              color={theme.palette.primary.main}
              sx={{
                fontSize: "38px",
                fontWeight: 500,
                textTransform: "none",
                mb: 4,
                textAlign: "center",
                fontFamily: "Cabin, sans-serif"
              }}>
              Something went wrong.
            </Typography>
            {error && (
              <Typography
                color={theme.palette.text.default}
                sx={{ textAlign: "center", mb: 0, fontSize: "16px" }}
              >
                {error.toString()}
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 7
              }}>
              <Button
                variant="outlined"
                onClick={() => window.location.reload()}
                startIcon={<RefreshIcon />}
                sx={{
                  borderRadius: "50px",
                  padding: "6px 30px",
                  fontSize: "16px",
                  border: "2px solid",
                  transition: "background 0.3s, transform 0.3s",
                  "&:hover": {
                    transform: "scale(1.05)",
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                Reload Page
              </Button>
            </Box>
          </Container>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default withThemeProps(ErrorBoundary);
